import { camelize } from '@ember/string';
import { filterSystemAndCurrentUser, matchingUsers } from './user';

export const findConversationByContacts = function (
  conversations,
  contacts,
  { isCouple = false } = {}
) {
  return conversations.find(conversation =>
    conversationHasExactContacts(conversation, contacts, { isCouple })
  );
};

/**
 * Same live Conversation as `other`: identical record, or both persisted
 * with the same id. Two unsaved records are not the same unless they are
 * the same object — missing ids must not collapse routes onto each other.
 */
export const isSameConversation = function (a, b) {
  return a === b || Boolean(a?.id && b?.id && String(a.id) === String(b.id));
};

export const conversationHasExactContacts = function (
  conversation,
  contacts,
  { isCouple = false } = {}
) {
  const designationMatch = Boolean(conversation.isCouple) === Boolean(isCouple);
  if (!designationMatch) return false;

  // Unsaved (virtual) conversations don't populate the `users` hasMany yet,
  // so we compare against the flattened contact roster instead. Contacts do
  // not include the current user, so lengths line up 1:1 with the argument.
  if (conversation.isNew && conversation.contacts?.length) {
    const members = conversation.contacts.flatMap(contact => contact.members ?? [contact]);
    return (
      members.length === contacts.length &&
      contacts.every(contact => members.find(member => matchingUsers(member, contact)))
    );
  }

  // Saved conversations always include the current user in `users`, so we
  // account for that with the +1.
  const lengthMatch = conversation.users.length === contacts.length + 1;
  return (
    lengthMatch &&
    contacts.every(contact => conversation.users.find(u => matchingUsers(u, contact)))
  );
};

export function jsonApiErrorTitle(error) {
  return error?.errors?.map(entry => entry.title).find(Boolean) ?? null;
}

export function inactiveReasonsFromConflicts(conflicts) {
  if (!Array.isArray(conflicts)) return [];

  return conflicts.map(inactiveReason => {
    if (!inactiveReason.meta) return inactiveReason;
    const camelizedMeta = Object.entries(inactiveReason.meta).reduce((acc, [key, value]) => {
      return {
        ...acc,
        ...{ [camelize(key)]: value },
      };
    }, {});
    return { ...inactiveReason, meta: camelizedMeta };
  });
}

export const conversationUserNames = function (names, isDirectCouple = false) {
  if (names.length <= 1) return names[0] ?? '';
  if (isDirectCouple) return names.join(' & ');
  return names.join(', ');
};

// Drops the viewer, including when they are one partner inside a couple slot.
// The other partner stays a person, so the title does not keep the viewer's name.
export function rosterWithoutCurrentUser(members, currentUser) {
  const expanded = (members ?? []).flatMap(member => {
    if (!member?.isCouple || !currentUser) return [member];

    const partners = member.members ?? [];
    if (!partners.some(person => matchingUsers(person, currentUser))) return [member];

    return partners.filter(person => !matchingUsers(person, currentUser));
  });

  if (!currentUser) return expanded;
  return filterSystemAndCurrentUser(expanded, currentUser);
}

// Names and profile links for the current viewer. Active members win. A group
// with nobody else active stays empty so the row can say "Just you" (COM-1053).
// A 1:1 or couple with nobody else active falls back to previous members so a
// disabled counterpart still has a name (COM-1459).
export function conversationMembersForDisplay(conversation, currentUser) {
  const activeMembers = rosterWithoutCurrentUser(
    conversation.collapsedMembers ?? conversation.users,
    currentUser
  );

  if (activeMembers.length || conversation.isGroup) {
    return activeMembers;
  }

  return rosterWithoutCurrentUser(conversation.previousMembers, currentUser);
}

export const firstNameAndLastNameInitial = function (name) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0];
  const lastPart = parts[parts.length - 1];
  const firstChar = Array.from(lastPart)[0];
  return firstChar ? `${parts[0]} ${firstChar}.` : `${parts[0]}.`;
};

const compareByName = (a, b) => (a.name ?? '').localeCompare(b.name ?? '');

const memberTier = (user, currentUser) => {
  if (currentUser && matchingUsers(user, currentUser)) {
    return 0;
  }

  return user.isClient ? 1 : 2;
};

export const sortConversationMembers = function (users, currentUser) {
  return [...users].sort((a, b) => {
    const tierDiff = memberTier(a, currentUser) - memberTier(b, currentUser);
    return tierDiff || compareByName(a, b);
  });
};
